// ============================================================
// fondos/capa.js — LA CAPA DEL FONDO dentro de la sección (DOM)
//
// Todo fondo animado vive en un `div.bg-layer`, primer hijo de su <section>. El contrato de capas
// de `css/site.css` (P2 del motor de fondos, gotcha 55) aísla la sección y deja la capa en
// `z-index: -1`: pinta justo encima del color o la foto de la sección y debajo de todo lo demás,
// velo de legibilidad incluido (el `::after` de la sección, `utils/velo.js`).
//
// Dentro de la capa, cada cosa en su sitio:
//   · el fondo que PINTA: el `<canvas class="bg-canvas">` de un lienzo, las manchas `.bg-capas` de un
//     fluido (`fondos/capas.js`), o la capa propia `.bg-css-layer` de una textura (`capaDePintura`);
//   · el overlay combinado, `.bg-overlay`: una textura sobre un fondo animado, con su color y su
//     opacidad propios (`applyOverlay`), ENCIMA del efecto (E9).
//
// LA OPACIDAD, A LA CAPA QUE PINTA Y NUNCA AL `.bg-layer`: el layer es el padre de las dos, así que
// atenuarlo atenuaría también el overlay. Hasta el P2 dentro vivía además el velo
// (`.bg-gradient-layer`), y por eso nació la regla (medido el 16-09: una opacidad del 40 % en el
// layer lavaba el contraste que calcula `llm_sanitizers._fix_foto_fondo`).
// NINGÚN `filter`: la clave `blur` del dato es la SUAVIDAD y la interpreta cada efecto (E10: el
// desenfoque de la capa dejaba viñeta en los bordes y bajaba bokeh de 30 a 17,9 fps a 100 px).
//
// Lo usa SOLO el motor (`fondos/motor.js`), que pinta igual el runtime y la vista previa del
// BG Studio: nadie más escribe dentro de la capa.
// ============================================================

import { TEXTURAS } from './catalogo.js';
import { pintarTexturaDeOverlay, esTexturaDeOverlay } from './texturas.js';

/** Fondos que PINTAN EN EL CONTENEDOR (no crean canvas): las texturas del catálogo. */
const FONDOS_CSS = new Set(TEXTURAS);

/** El `.bg-layer` de la sección (hijo directo), o null. */
export function layerDe(section) {
    if (!section || !section.children) return null;
    for (let i = 0; i < section.children.length; i++) {
        const hijo = section.children[i];
        if (hijo.classList && hijo.classList.contains('bg-layer')) return hijo;
    }
    return null;
}

/** Deja la capa como recién creada: sin contenido y sin estilos de una pasada anterior. */
export function vaciarLayer(layer) {
    if (!layer) return;
    layer.innerHTML = '';
    layer.style.background = '';
    layer.style.backgroundImage = '';
    layer.style.backgroundSize = '';
    layer.style.opacity = '';
    // Hasta E1b el blur se escribía en el LAYER: uno reutilizado se quedaba desenfocado aunque el
    // bloque ya no pidiera blur.
    layer.style.filter = '';
    layer.style.mixBlendMode = '';
}

/**
 * La capa de la sección, VACÍA y lista para pintar: la que ya tuviera (vaciada) o una nueva,
 * insertada como primer hijo para quedar detrás del contenido.
 */
export function asegurarLayer(section) {
    let layer = layerDe(section);
    if (layer) {
        vaciarLayer(layer);
        return layer;
    }
    layer = document.createElement('div');
    layer.className = 'bg-layer';
    layer.style.pointerEvents = 'none';
    section.insertBefore(layer, section.firstChild);
    return layer;
}

/**
 * Contenedor donde debe pintar el fondo `animType` dentro de `layer`.
 * Animado → el propio layer (el efecto cuelga ahí su `<canvas>`).
 * Textura → una capa propia `.bg-css-layer`, para poder atenuarla sin tocar el overlay.
 * Se posiciona INLINE a propósito: un sitio ya publicado puede estar sirviendo un `site.css`
 * cacheado sin esa regla, y sin caja el fondo CSS no se vería.
 *
 * @param {HTMLElement} layer    - div.bg-layer
 * @param {string}      animType - id del catálogo
 */
export function capaDePintura(layer, animType) {
    if (!layer || !FONDOS_CSS.has(animType)) return layer;
    let capa = layer.querySelector('.bg-css-layer');
    if (!capa) {
        capa = document.createElement('div');
        capa.className = 'bg-css-layer';
        capa.style.cssText = 'position:absolute; inset:0; pointer-events:none;';
        layer.insertBefore(capa, layer.firstChild);
    }
    return capa;
}

/**
 * La capa que YA está pintando dentro de `layer`: el `<canvas>` de un lienzo, las manchas de un
 * fluido o la capa CSS de una textura. Es la única a la que puede aplicarse la opacidad: el overlay
 * combinado (`.bg-overlay`) es HERMANO suyo dentro del mismo layer, así que todo lo que se le ponga
 * al layer se lo come también el overlay.
 *
 * @param {HTMLElement} layer - div.bg-layer
 * @returns {HTMLElement|null}
 */
export function capaPintada(layer) {
    if (!layer) return null;
    return layer.querySelector('.bg-canvas') || layer.querySelector('.bg-capas') || layer.querySelector('.bg-css-layer');
}

/**
 * La opacidad del fondo (0-100, tal como la guarda `bg_animation_config.opacity`), SIN tocar el
 * overlay combinado: va a la capa que pinta —`.bg-canvas` o `.bg-css-layer`— y jamás a
 * `layer.style.opacity`. Sin valor, la capa vuelve a su opacidad natural (100 %): el motor la
 * reaplica cuando la vista previa del panel cambia y cuando se restaura lo guardado.
 * Sin capa que pintar no hace nada: es preferible un fondo a plena fuerza a un overlay lavado.
 *
 * @param {HTMLElement} layer   - div.bg-layer
 * @param {number}      [opacity] - 0-100
 */
export function aplicarOpacidadDeFondo(layer, opacity) {
    const pintor = capaPintada(layer);
    if (!pintor) return;
    pintor.style.opacity = (opacity === undefined || opacity === null) ? '' : opacity / 100;
}

/**
 * Aplica (o elimina) el overlay combinado en `div.bg-overlay` dentro de `layer`.
 * Con `overlayId` 'none', vacío o desconocido, elimina el que hubiera.
 *
 * @param {HTMLElement} layer
 * @param {string}      overlayId - id de `texturas.js::TEXTURAS_DE_OVERLAY`
 * @param {string}      accent    - color del overlay
 * @param {boolean}     isDark
 * @returns {HTMLElement|null} el `.bg-overlay` pintado, o null si no hay
 */
export function applyOverlay(layer, overlayId, accent, isDark) {
    if (!layer) return null;
    let overlayEl = layer.querySelector('.bg-overlay');

    if (!overlayId || overlayId === 'none' || !esTexturaDeOverlay(overlayId)) {
        if (overlayEl) overlayEl.remove();
        return null;
    }

    if (!overlayEl) {
        overlayEl = document.createElement('div');
        overlayEl.className = 'bg-overlay';
    }
    // ENCIMA del efecto (E9): la textura se combina SOBRE el movimiento. Debajo apenas se veía
    // (0-17 % sobre los fondos que rellenan: el efecto la tapaba). El canvas y las manchas llevan
    // `z-index:0` en línea, así que el overlay va a z 1 —en `css/site.css` y aquí, en línea: un
    // publicado puede servir un site.css cacheado— y además el ÚLTIMO del layer, para que el orden
    // del árbol diga lo mismo que el z-index.
    if (layer.lastChild !== overlayEl) layer.appendChild(overlayEl);

    // Limpiar estilos previos (color y opacidad de una pasada anterior) antes de pintar.
    overlayEl.removeAttribute('style');
    pintarTexturaDeOverlay(overlayEl, overlayId, accent, isDark);
    overlayEl.style.zIndex = '1';
    return overlayEl;
}
